import { createFileRoute, Link } from "@tanstack/react-router"
import { ArrowLeft } from "lucide-react"

import {
  Badge,
  Card,
  EmptyState,
  ErrorState,
  PageHeader,
  Skeleton,
} from "@/components/ui"
import { useEvent } from "@/features/events/api"
import { EventActions } from "@/features/events/event-actions"
import { EventCosts } from "@/features/events/event-costs"
import { EventDetails } from "@/features/events/event-details"
import { EventLearners } from "@/features/events/event-learners"
import { EventNumber } from "@/features/events/event-number"
import { STATUS_LABELS, STATUS_TONES } from "@/lib/constants"
import { DocumentsPanel } from "@/features/documents/panel"

export const Route = createFileRoute("/events/$eventId")({ component: EventScreen })

function EventScreen() {
  const { eventId } = Route.useParams()
  const id = Number(eventId)
  const query = useEvent(id)

  return (
    <>
      <Link
        to="/events"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Events
      </Link>

      {!Number.isInteger(id) ? (
        <Card>
          <ErrorState
            title="That is not an event"
            description="That web address does not name a training event."
          />
        </Card>
      ) : query.isPending ? (
        <LoadingEvent />
      ) : query.isError ? (
        <Card>
          <ErrorState
            title="This event did not load"
            description="Something went wrong loading this event. Tell your administrator if it keeps happening."
            onRetry={() => void query.refetch()}
          />
        </Card>
      ) : !query.data ? (
        <Card>
          <EmptyState
            title="No such event"
            description="It may have been deleted since this link was made."
          />
        </Card>
      ) : (
        <div className="space-y-6">
          <div>
            <PageHeader
              eyebrow="Training"
              title={query.data.event_number || "Not numbered yet"}
              actions={<EventActions event={query.data} />}
            />
            <div className="-mt-2 mb-2 flex flex-wrap items-center gap-3">
              <Badge tone={STATUS_TONES[query.data.status] ?? "neutral"}>
                {STATUS_LABELS[query.data.status] ?? query.data.status}
              </Badge>
              <span className="text-sm text-muted-foreground">
                {query.data.learner_count === 1
                  ? "1 learner"
                  : `${query.data.learner_count} learners`}
              </span>
              <EventNumber event={query.data} hideNumber />
            </div>
          </div>

          {/* Details take the width, costs the column: the cost lines are short
              and the details are the ones that wrap. */}
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <EventDetails event={query.data} />
            </div>
            <EventCosts event={query.data} />
          </div>

          <EventLearners eventId={id} eventStatus={query.data.status} />

          <DocumentsPanel
            parentType="events"
            parentId={id}
            frozenNote={
              query.data.status === "completed" || query.data.status === "cancelled"
                ? "This event is closed, so its documents can no longer be changed."
                : undefined
            }
          />
        </div>
      )}
    </>
  )
}

function LoadingEvent() {
  return (
    <div className="space-y-6" aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading the event</span>
      <Skeleton className="h-8 w-64" />
      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2 p-5">
          <Skeleton className="h-5 w-24" />
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {Array.from({ length: 6 }).map((_, index) => (
              <Skeleton key={index} className="h-10" />
            ))}
          </div>
        </Card>
        <Card className="p-5">
          <Skeleton className="h-5 w-20" />
          <div className="mt-4 space-y-3">
            {Array.from({ length: 8 }).map((_, index) => (
              <Skeleton key={index} className="h-4" />
            ))}
          </div>
        </Card>
      </div>
    </div>
  )
}
